import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Row, Col, Button, Alert, AutoComplete } from 'antd';
import DividerBlock from '../../../divider-block';
import FieldBlock from '../../../field-block';
import NumberInputField from '../../../number-input-field';
import SelectField from '../../../select-field';
import i18n from '../../../../i18n';
import { crossDockService } from '../../../../services/cross-dock.services';
import { uuidHelper } from '../../../../helpers/uuid.helper';

function createPalletItem() {
  return {
    itemUuid: uuidHelper.generateUUID(),
    name: '',
    presetId: null,
    boxesCount: '',
    boxLengthCm: '',
    boxWidthCm: '',
    boxHeightCm: '',
    boxWeightKg: '',
    assessedValue: '',
  };
}

const calcTotalWeight = (items) =>
  items.reduce((sum, item) => sum + (parseFloat(item.boxWeightKg) || 0) * (parseInt(item.boxesCount) || 0), 0);

const itemCardRemoveButtonStyle = {
  width: '50px',
  height: '35px',
  backgroundColor: 'red',
  color: 'white',
  borderRadius: '5px',
  border: 'none',
  float: 'right',
  boxShadow: 'none',
};

export default function BulkItems({
  storesList,
  palletGroups,
  setPalletGroups,
  fieldsErrors,
  dividerStyle,
  legalEntityId,
}) {
  const [presetOptions, setPresetOptions] = useState({});
  const [openDropdown, setOpenDropdown] = useState({});
  const [validationWarnings, setValidationWarnings] = useState({});
  const debounceRefs = useRef({});
  const selectingRef = useRef({});

  // Валидация палета по сумме объёмов коробок
  const validatePalletGroup = useCallback(async (group) => {
    if (!group.items || group.items.length === 0) return;

    const totalBoxes = group.items.reduce((sum, item) => sum + (parseInt(item.boxesCount) || 0), 0);
    if (totalBoxes === 0) return;

    const hasDimensions = group.items.some(item => {
      const l = parseFloat(item.boxLengthCm);
      const w = parseFloat(item.boxWidthCm);
      const h = parseFloat(item.boxHeightCm);
      return l > 0 && w > 0 && h > 0;
    });
    if (!hasDimensions) return;

    const boxes = group.items.map(item => ({
      boxesCount: parseInt(item.boxesCount) || 0,
      weightKg: parseFloat(item.boxWeightKg) || 0,
      lengthCm: parseFloat(item.boxLengthCm) || 0,
      widthCm: parseFloat(item.boxWidthCm) || 0,
      heightCm: parseFloat(item.boxHeightCm) || 0,
    }));

    try {
      const result = await crossDockService.validatePallet(boxes);
      setValidationWarnings(prev => ({
        ...prev,
        [group.groupUuid]: result.is_valid ? null : result.warning,
      }));
    } catch {
      setValidationWarnings(prev => ({
        ...prev,
        [group.groupUuid]: null,
      }));
    }
  }, []);

  useEffect(() => {
    palletGroups.forEach(group => {
      if (debounceRefs.current[group.groupUuid]) {
        clearTimeout(debounceRefs.current[group.groupUuid]);
      }
      debounceRefs.current[group.groupUuid] = setTimeout(() => {
        validatePalletGroup(group);
      }, 500);
    });
  }, [palletGroups, validatePalletGroup]);

  const getItemErrors = (nameField, key) => {
    return (fieldsErrors && typeof fieldsErrors['item.'+nameField+'.'+key] !== "undefined")
      ? fieldsErrors['item.'+nameField+'.'+key][0]
      : null;
  };

  const getBoxErrors = (nameField, key) => {
    return (fieldsErrors && typeof fieldsErrors['item.'+nameField+'.'+key] !== "undefined")
      ? fieldsErrors['item.'+nameField+'.'+key][0]
      : null;
  };

  const getFieldErrors = (nameField) => {
    return (fieldsErrors && typeof fieldsErrors[nameField] !== "undefined")
      ? fieldsErrors[nameField][0]
      : null;
  };

  const addPalletGroup = () => {
    const group = {
      groupUuid: uuidHelper.generateUUID(),
      storeId: null,
      items: [createPalletItem()],
      totalWeightKg: 0,
    };
    setPalletGroups(prev => [...prev, group]);
  };

  const removePalletGroup = (groupUuid) => {
    setPalletGroups(prev => prev.filter(g => g.groupUuid !== groupUuid));
  };

  const updateGroupField = (groupUuid, field, value) => {
    setPalletGroups(prev => prev.map(g =>
      g.groupUuid === groupUuid ? { ...g, [field]: value } : g
    ));
  };

  const addItemToGroup = (groupUuid) => {
    setPalletGroups(prev => prev.map(g => {
      if (g.groupUuid !== groupUuid) return g;
      const items = [...g.items, createPalletItem()];
      return { ...g, items, totalWeightKg: calcTotalWeight(items) };
    }));
  };

  const removeItemFromGroup = (groupUuid, itemUuid) => {
    setPalletGroups(prev => prev.map(g => {
      if (g.groupUuid !== groupUuid) return g;
      const items = g.items.filter(item => item.itemUuid !== itemUuid);
      return { ...g, items, totalWeightKg: calcTotalWeight(items) };
    }));
  };

  const updateItemField = (groupUuid, itemUuid, field, value) => {
    setPalletGroups(prev => prev.map(g => {
      if (g.groupUuid !== groupUuid) return g;
      const items = g.items.map(item =>
        item.itemUuid === itemUuid ? { ...item, [field]: value } : item
      );
      return { ...g, items, totalWeightKg: calcTotalWeight(items) };
    }));
  };

  const handleNameSearch = (groupUuid, itemUuid, value) => {
    const key = `${groupUuid}-${itemUuid}`;
    if (debounceRefs.current[key]) {
      clearTimeout(debounceRefs.current[key]);
    }

    debounceRefs.current[key] = setTimeout(() => {
      if (value && legalEntityId) {
        crossDockService.getPreset(legalEntityId, value)
          .then(res => {
            const options = res.map(item => ({
              value: item.Name,
              label: item.Name,
              id: item.Id,
            }));
            setPresetOptions(prev => ({ ...prev, [key]: options }));
            setOpenDropdown(prev => ({ ...prev, [key]: options.length > 0 }));
          })
          .catch(() => {
            setPresetOptions(prev => ({ ...prev, [key]: [] }));
          });
      } else {
        setPresetOptions(prev => ({ ...prev, [key]: [] }));
      }
    }, 500);
  };

  const handlePresetSelect = (groupUuid, itemUuid, value, option) => {
    const key = `${groupUuid}-${itemUuid}`;
    selectingRef.current[key] = true;
    setOpenDropdown(prev => ({ ...prev, [key]: false }));
    updateItemField(groupUuid, itemUuid, 'name', value);
    updateItemField(groupUuid, itemUuid, 'presetId', option.id);
    setTimeout(() => { selectingRef.current[key] = false; }, 300);
  };

  const renderItemCard = (groupUuid, item, index) => {
    const key = `${groupUuid}-${item.itemUuid}`;
    const options = presetOptions[key] || [];

    return (
      <div
        key={item.itemUuid}
        style={{
          border: '1px solid rgba(0, 0, 0, 0.1)',
          marginTop: '10px',
          padding: '15px',
          borderRadius: '5px',
        }}
      >
        <Row gutter={[16, 24]}>
          <Col lg={20} md={24} sm={24} sx={24}>
            <FieldBlock keyNum={`${groupUuid}.${item.itemUuid}`} name={`name`} title={i18n.t('Наименование номенклатуры')} getErrors={getBoxErrors}>
              <AutoComplete
                value={item.name}
                filterOption={false}
                open={openDropdown[key] || false}
                getPopupContainer={(triggerNode) => triggerNode.parentNode}
                onDropdownVisibleChange={(visible) => {
                  if (!selectingRef.current[key]) {
                    setOpenDropdown(prev => ({ ...prev, [key]: visible }));
                  }
                }}
                onChange={(value) => {
                  updateItemField(groupUuid, item.itemUuid, 'name', value);
                  handleNameSearch(groupUuid, item.itemUuid, value);
                }}
                onSelect={(value, option) => handlePresetSelect(groupUuid, item.itemUuid, value, option)}
                style={{ width: '100%' }}
                placeholder={i18n.t('Наименование номенклатуры')}
              >
                {options.map(opt => (
                  <AutoComplete.Option key={opt.id} value={opt.value} id={opt.id}>
                    {opt.label}
                  </AutoComplete.Option>
                ))}
              </AutoComplete>
            </FieldBlock>
          </Col>
          <Col lg={4} md={24} sm={24} sx={24}>
            <Button
              type="primary"
              danger
              icon="close"
              onClick={() => removeItemFromGroup(groupUuid, item.itemUuid)}
              style={itemCardRemoveButtonStyle}
            />
          </Col>
        </Row>
        <Row gutter={[16, 24]} style={{ marginTop: '15px' }}>
          <Col lg={5} md={24} sm={24} sx={24}>
            <NumberInputField
              name={`boxesCount`}
              keyNum={`${groupUuid}.${item.itemUuid}`}
              label={i18n.t('Кол-во коробок')}
              value={item.boxesCount}
              setValue={value => updateItemField(groupUuid, item.itemUuid, 'boxesCount', value)}
              getFieldErrors={getBoxErrors}
            />
          </Col>
          <Col lg={4} md={24} sm={24} sx={24}>
            <NumberInputField
              name={`boxLengthCm`}
              keyNum={`${groupUuid}.${item.itemUuid}`}
              label={i18n.t('Длина (см)')}
              value={item.boxLengthCm}
              setValue={value => updateItemField(groupUuid, item.itemUuid, 'boxLengthCm', value)}
              getFieldErrors={getBoxErrors}
            />
          </Col>
          <Col lg={4} md={24} sm={24} sx={24}>
            <NumberInputField
              name={`boxWidthCm`}
              keyNum={`${groupUuid}.${item.itemUuid}`}
              label={i18n.t('Ширина (см)')}
              value={item.boxWidthCm}
              setValue={value => updateItemField(groupUuid, item.itemUuid, 'boxWidthCm', value)}
              getFieldErrors={getBoxErrors}
            />
          </Col>
          <Col lg={4} md={24} sm={24} sx={24}>
            <NumberInputField
              name={`boxHeightCm`}
              keyNum={`${groupUuid}.${item.itemUuid}`}
              label={i18n.t('Высота (см)')}
              value={item.boxHeightCm}
              setValue={value => updateItemField(groupUuid, item.itemUuid, 'boxHeightCm', value)}
              getFieldErrors={getBoxErrors}
            />
          </Col>
          <Col lg={4} md={24} sm={24} sx={24}>
            <NumberInputField
              name={`boxWeightKg`}
              keyNum={`${groupUuid}.${item.itemUuid}`}
              label={i18n.t('Вес (кг)')}
              value={item.boxWeightKg}
              setValue={value => updateItemField(groupUuid, item.itemUuid, 'boxWeightKg', value)}
              getFieldErrors={getBoxErrors}
            />
          </Col>
          <Col lg={7} md={24} sm={24} sx={24}>
            <NumberInputField
              name={`assessedValue`}
              keyNum={`${groupUuid}.${item.itemUuid}`}
              label={i18n.t('Оценочная стоимость')}
              value={item.assessedValue}
              setValue={value => updateItemField(groupUuid, item.itemUuid, 'assessedValue', value)}
              getFieldErrors={getBoxErrors}
            />
          </Col>
        </Row>
      </div>
    );
  };

  const renderPalletGroup = (group) => {
    const warning = validationWarnings[group.groupUuid];

    return (
      <div
        key={group.groupUuid}
        style={{
          marginBottom: '30px',
          border: '1px solid #d9d9d9',
          padding: '20px',
          borderRadius: '8px',
        }}
      >
        <Row gutter={[16, 24]}>
          <Col lg={16} md={24} sm={24} sx={24}>
            <FieldBlock name={`store-${group.groupUuid}`} title={i18n.t('Торговая точка')} getErrors={() => null}>
              <SelectField
                name="storeId"
                selectedValue={group.storeId}
                values={storesList}
                onChange={value => updateGroupField(group.groupUuid, 'storeId', value)}
                getErrors={getItemErrors}
                keyNum={group.groupUuid}
                style={{ width: '100%' }}
              />
            </FieldBlock>
          </Col>
          <Col lg={4} md={24} sm={24} sx={24}>
            <FieldBlock name={`skuCount-${group.groupUuid}`} title={i18n.t('Кол-во SKU')} getErrors={() => null}>
              <span style={{ lineHeight: '32px' }}>{group.items.length}</span>
            </FieldBlock>
          </Col>
          <Col lg={4} md={24} sm={24} sx={24}>
            <Button
              type="primary"
              danger
              icon="close"
              onClick={() => removePalletGroup(group.groupUuid)}
              style={itemCardRemoveButtonStyle}
            />
          </Col>
        </Row>

        {warning && (
        <Row>
          <Col>
          <Alert
            message={warning}
            type="warning"
            showIcon
            style={{ marginBottom: '20px' }}
          />
          </Col>
        </Row>
        )}

        {group.items.map((item, index) => renderItemCard(group.groupUuid, item, index))}

        <Row gutter={[16, 24]} style={{ marginTop: '16px' }}>
          <Col lg={24} md={24} sm={24} sx={24}>
            <Button
              type="dashed"
              onClick={() => addItemToGroup(group.groupUuid)}
              style={{ width: '100%' }}
              icon="plus"
            >
              {i18n.t('Добавить номенклатуру в палет')}
            </Button>
          </Col>
        </Row>
      </div>
    );
  };

  if (!storesList || storesList.length === 0) {
    return (
      <p style={{ padding: '20px', color: '#888' }}>
        {i18n.t('Выберите регион и город для загрузки магазинов')}
      </p>
    );
  }

  return (
    <>
      <span className="divider-label">{i18n.t('Палеты (валом)')}</span>
      <DividerBlock style={dividerStyle} />
      {fieldsErrors && fieldsErrors['items'] && (
        <Alert
          message={fieldsErrors['items'][0]}
          type="error"
          showIcon
          style={{ marginBottom: '20px' }}
        />
      )}
      {palletGroups.map(group => renderPalletGroup(group))}

      <Row gutter={[16, 24]} style={{ marginBottom: '24px' }}>
        <Col lg={24} md={24} sm={24} sx={24}>
          <Button
            type="primary"
            onClick={addPalletGroup}
            style={{
              width: '100%',
              height: '43px',
              background: '#1CAA53',
              borderRadius: '10px',
              borderColor: 'transparent',
              boxShadow: 'none',
            }}
            icon="plus"
          >
            {i18n.t('Добавить палет')}
          </Button>
        </Col>
      </Row>
    </>
  );
}